'use client';

import { useState, useSyncExternalStore, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCurrencyFormatter } from '@/lib/currency-formatter';
import { calculateInvoiceTotals } from '@/lib/helpers/invoice-math';
import { getDateFormat } from '@/lib/date-formatter';
import { useTranslations } from '@/hooks/useTranslations';
import { BuilderState, PLACEHOLDER_BUILDER_STATE } from './types';
import { SignUpPromptDialog } from './sign-up-prompt-dialog';
import {
  getDownloadCount,
  incrementDownloadCount,
} from './use-builder-storage';
import type { IInvoice } from '@/types/invoice';
import type { IClient } from '@/types/client';

const BuilderPDFDownload = dynamic(
  () => import('./builder-pdf-download').then((m) => m.BuilderPDFDownload),
  { ssr: false }
);

const PDF_TRANSLATIONS = {
  no: 'No.',
  invoiceFrom: 'Invoice from',
  taxId: 'Tax ID:',
  invoiceTo: 'Invoice to',
  registrationNumber: 'Reg. No.:',
  invoiceNumber: 'Invoice No.:',
  issuedAt: 'Issued:',
  fromDate: 'Service:',
  dueDate: 'Due:',
  bankName: 'Bank:',
  account: 'Account:',
  bic: 'BIC:',
  email: 'Email:',
  items: 'Description',
  cost: 'Price',
  price: 'Price',
  qty: 'Qty',
  unit: 'Unit',
  total: 'Total',
  subtotal: 'Subtotal',
  tax: 'Tax',
  vatNotice: '',
  issuedBy: 'Issued by',
  receivedBy: 'Received by',
  scanToPay: 'Scan to pay',
};

function pv(
  actual: string,
  placeholder: string
): { value: string; ip: boolean } {
  return actual.trim()
    ? { value: actual, ip: false }
    : { value: placeholder, ip: true };
}

const pc = (ip: boolean) => (ip ? 'italic' : '');

function buildMockData(state: BuilderState) {
  const mockInvoice: IInvoice = {
    id: 'preview',
    userId: 'preview',
    clientId: 'preview',
    invoiceNumber: state.invoiceNumber,
    invoiceType: state.invoiceType || 'Invoice',
    status: 'UNPAID',
    serviceFrom: state.serviceFrom
      ? new Date(state.serviceFrom)
      : new Date(state.issuedDate),
    serviceTo: state.serviceTo ? new Date(state.serviceTo) : null,
    dueDate: new Date(state.dueDate),
    issuedDate: new Date(state.issuedDate),
    items: state.items.map((item) => ({
      ...item,
      total: item.qty * item.price,
    })),
    ddvAdditionalInput: null,
  };

  const mockClient: IClient = {
    id: 'preview',
    userId: 'preview',
    name: state.clientName,
    address: state.clientAddress,
    city: state.clientCity,
    country: state.clientCountry,
    zipCode: state.clientZipCode,
    taxNumber: state.clientTaxNumber,
    email: null,
    registrationNumber: null,
  };

  const mockSession = {
    user: {
      id: 'preview',
      name: state.companyName,
      email: state.email,
      companyName: state.companyName,
      companyAddress: state.companyAddress,
      city: state.city,
      country: state.country,
      zipCode: state.zipCode,
      taxId: state.taxId,
      bankName: state.bankName,
      accountNumber: state.accountNumber,
      bic: state.bic,
      logo: null,
      signInvoice: false,
      currency: state.currency,
    },
    expires: '',
  };

  return { mockInvoice, mockClient, mockSession };
}

interface BuilderPreviewProps {
  state: BuilderState;
  locale: string;
  t: (key: string) => string;
}

const FREE_DOWNLOAD_LIMIT = 3;

export function BuilderPreview({ state, locale, t }: BuilderPreviewProps) {
  const tOverview = useTranslations('InvoiceOverview');
  const [showDialog, setShowDialog] = useState(false);
  const [downloadCount, setDownloadCount] = useState<number | null>(null);
  const subscribe = useCallback((cb: () => void) => {
    window.addEventListener('storage', cb);
    return () => window.removeEventListener('storage', cb);
  }, []);
  const clientCount = useSyncExternalStore(
    subscribe,
    () => getDownloadCount(),
    () => null as unknown as number
  );

  // Sync from external store on client, keep null on server
  const count = downloadCount ?? clientCount;
  const mounted = count !== null && !isNaN(count);
  const limitReached = mounted && count >= FREE_DOWNLOAD_LIMIT;
  const downloadsLeft = mounted
    ? Math.max(0, FREE_DOWNLOAD_LIMIT - count)
    : FREE_DOWNLOAD_LIMIT;

  const p = PLACEHOLDER_BUILDER_STATE;

  const d = {
    companyName: pv(state.companyName, p.companyName),
    companyAddress: pv(state.companyAddress, p.companyAddress),
    zipCode: pv(state.zipCode, p.zipCode),
    city: pv(state.city, p.city),
    country: pv(state.country, p.country),
    taxId: pv(state.taxId, p.taxId),
    bankName: pv(state.bankName, p.bankName),
    accountNumber: pv(state.accountNumber, p.accountNumber),
    bic: pv(state.bic, p.bic),
    email: pv(state.email, p.email),
    invoiceNumber: pv(state.invoiceNumber, p.invoiceNumber),
    invoiceType: pv(state.invoiceType, p.invoiceType),
    serviceFrom: pv(state.serviceFrom, p.serviceFrom),
    serviceTo: state.serviceTo || p.serviceTo,
    issuedDate: pv(state.issuedDate, p.issuedDate),
    dueDate: pv(state.dueDate, p.dueDate),
    clientName: pv(state.clientName, p.clientName),
    clientAddress: pv(state.clientAddress, p.clientAddress),
    clientZipCode: pv(state.clientZipCode, p.clientZipCode),
    clientCity: pv(state.clientCity, p.clientCity),
    clientCountry: pv(state.clientCountry, p.clientCountry),
    clientTaxNumber: pv(state.clientTaxNumber, p.clientTaxNumber),
  };

  const hasRealItems = state.items.some((item) => item.description.trim());
  const displayItems = hasRealItems ? state.items : p.items;
  const itemsArePlaceholder = !hasRealItems;

  const fmt = (amount: number) =>
    getCurrencyFormatter(
      amount,
      state.currency as 'EUR' | 'USD' | 'RSD' | 'GBP'
    );

  const itemsWithTotals = displayItems.map((item) => ({
    ...item,
    total: item.qty * item.price,
  }));

  const { subtotal, tax, total } = calculateInvoiceTotals(itemsWithTotals);
  const { mockInvoice, mockClient, mockSession } = buildMockData(state);

  const fileName =
    `${state.invoiceType}_${state.invoiceNumber}_${state.clientName || 'invoice'}.pdf`.replace(
      /\s+/g,
      '_'
    );

  return (
    <div className="flex flex-col gap-4">
      {/* Invoice preview — same layout as SingleInvoices, always ~20% taller than form */}
      <div
        className="bg-muted/30 rounded-xl p-3 overflow-auto"
        style={{ minHeight: 460 }}
      >
        <div className="bg-white text-gray-800 border rounded overflow-hidden mx-auto p-4 font-sans flex justify-between flex-col text-[10px]">
          <div>
            {/* Row 1: Invoice from / Invoice to */}
            <div className="flex gap-3 justify-between">
              <div className="flex-1">
                <h2 className="mb-0.5">{tOverview('invoiceFrom')}</h2>
                <p className={`font-bold ${pc(d.companyName.ip)}`}>
                  {d.companyName.value}
                </p>
                <p className={pc(d.companyAddress.ip)}>
                  {d.companyAddress.value},
                </p>
                <p className={pc(d.zipCode.ip || d.city.ip || d.country.ip)}>
                  {[d.zipCode.value, d.city.value, d.country.value]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                <p className={pc(d.taxId.ip)}>
                  {tOverview('taxId')} {d.taxId.value}
                </p>
              </div>

              <div className="flex-1">
                <h2 className="mb-0.5">{tOverview('invoiceTo')}</h2>
                <p className={`font-bold ${pc(d.clientName.ip)}`}>
                  {d.clientName.value}
                </p>
                <p className={pc(d.clientAddress.ip)}>
                  {d.clientAddress.value},
                </p>
                <p
                  className={pc(
                    d.clientZipCode.ip || d.clientCity.ip || d.clientCountry.ip
                  )}
                >
                  {[
                    d.clientZipCode.value,
                    d.clientCity.value,
                    d.clientCountry.value,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                <p className={pc(d.clientTaxNumber.ip)}>
                  {tOverview('taxId')} {d.clientTaxNumber.value}
                </p>
              </div>
            </div>

            {/* Row 2: Bank info / Invoice meta */}
            <div className="flex gap-3 justify-between mt-3">
              <div className="flex-1">
                <p className={pc(d.bankName.ip)}>
                  {tOverview('bankName')} {d.bankName.value}
                </p>
                <p className={pc(d.accountNumber.ip)}>
                  {tOverview('account')} {d.accountNumber.value}
                </p>
                <p className={pc(d.bic.ip)}>
                  {tOverview('bic')} {d.bic.value}
                </p>
                <p className={pc(d.email.ip)}>
                  {tOverview('email')} {d.email.value}
                </p>
              </div>

              <div className="flex-1">
                <p className={pc(d.invoiceNumber.ip)}>
                  {tOverview('invoiceNumber')}{' '}
                  <span className="font-bold">#{d.invoiceNumber.value}</span>
                </p>
                <p className={pc(d.issuedDate.ip)}>
                  {tOverview('issuedAt')}{' '}
                  {getDateFormat(d.issuedDate.value, locale)}
                </p>
                <p className={pc(d.serviceFrom.ip)}>
                  {tOverview('fromDate')}{' '}
                  {getDateFormat(d.serviceFrom.value, locale)}
                  {d.serviceTo && ` - ${getDateFormat(d.serviceTo, locale)}`}
                </p>
                <p className={pc(d.dueDate.ip)}>
                  {tOverview('dueDate')}{' '}
                  {getDateFormat(d.dueDate.value, locale)}
                </p>
              </div>
            </div>

            {/* Items */}
            <div className="mt-4">
              <div className="flex justify-between items-center border-b border-t p-2">
                <span className="flex-1">{tOverview('no')}</span>
                <span className="flex-[0_0_40%]">{tOverview('items')}</span>
                <span className="flex-1">{tOverview('cost')}</span>
                <span className="flex-1">{tOverview('qty')}</span>
                <span className="flex-1">{tOverview('unit')}</span>
                <span className="flex-1 text-right">{tOverview('total')}</span>
              </div>

              <div className={itemsArePlaceholder ? 'italic' : ''}>
                {itemsWithTotals.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between p-2 border-b"
                  >
                    <span className="flex-1">{index + 1}</span>
                    <span className="flex-[0_0_40%]">{item.description}</span>
                    <span className="flex-1">{fmt(item.price)}</span>
                    <span className="flex-1">{item.qty}</span>
                    <span className="flex-1">{item.unit}</span>
                    <span className="flex-1 text-right">{fmt(item.total)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div
              className={`flex flex-col justify-end items-end w-52 border border-t-0 ml-auto ${itemsArePlaceholder ? 'italic' : ''}`}
            >
              <div className="p-2 w-full flex justify-between border-b">
                <span>{tOverview('subtotal')}</span>
                <span>{fmt(subtotal)}</span>
              </div>
              <div className="p-2 w-full flex justify-between border-b">
                <span>{tOverview('tax')}</span>
                <span>{fmt(tax)}</span>
              </div>
              <div className="p-2 w-full flex justify-between font-semibold">
                <span>{tOverview('total')}</span>
                <span>{fmt(total)}</span>
              </div>
            </div>
          </div>

          {/* VAT notice — only when no tax is charged */}
          {tax === 0 && (
            <div className="mt-8">
              <p>{tOverview('vatNotice')}</p>
            </div>
          )}

          {/* Footer */}
          <div className="w-full mt-6">
            <div
              className={`text-center border-t pt-2 ${pc(d.companyName.ip)}`}
            >
              <p>{d.companyName.value}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Download button */}
      {!mounted ? (
        <Button className="w-full gap-2" size="lg" disabled>
          <FileText className="w-4 h-4" />
          {t('downloadFree')}
        </Button>
      ) : limitReached ? (
        <Button
          className="w-full gap-2"
          size="lg"
          onClick={() => setShowDialog(true)}
        >
          <Download className="w-4 h-4" />
          {t('downloadFree')}
        </Button>
      ) : (
        <div className="flex flex-col gap-1.5">
          <BuilderPDFDownload
            invoice={mockInvoice}
            client={mockClient}
            session={mockSession as any}
            fileName={fileName}
            locale={locale}
            translations={PDF_TRANSLATIONS}
            label={t('downloadFree')}
            onDownload={() => {
              incrementDownloadCount();
              setDownloadCount(getDownloadCount());
            }}
          />
          <p className="text-xs text-center text-muted-foreground">
            {t('downloadsRemaining').replace('{count}', String(downloadsLeft))}
          </p>
        </div>
      )}

      <SignUpPromptDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        locale={locale}
        t={t}
      />
    </div>
  );
}
